import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import AuthShell from "@/components/AuthShell";
import { Link, useNavigate } from "react-router-dom";
import { useHashToken } from "@/hooks/useHashToken";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { graphqlClient } from "@/lib/graphql";
import { graphql } from "@/__generated__";

const VERIFY_EMAIL = graphql(`
  mutation VerifyEmail($token: String!) {
    verifyEmail(input: { token: $token }) {
      success
      errors
    }
  }
`);

type Status = "verifying" | "success" | "error";

const VerifyEmail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = useHashToken();
  const [status, setStatus] = useState<Status>("verifying");
  // React 18 StrictMode double-invokes effects in dev; guard so we fire the
  // mutation once and don't flash an error from a redundant second call.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setStatus("error");
      return;
    }
    (async () => {
      try {
        const { verifyEmail } = await graphqlClient.request(VERIFY_EMAIL, { token });
        if (verifyEmail.success) {
          setStatus("success");
          // A signed-in user's cached `me` still says unverified — refresh it so
          // the banner and booking gate clear without a manual reload.
          queryClient.invalidateQueries({ queryKey: ["me"] });
        } else {
          setStatus("error");
        }
      } catch (err) {
        console.error("VerifyEmail error:", err);
        setStatus("error");
      }
    })();
  }, [token, queryClient]);

  return (
    <AuthShell>
      <Card className="border-border/50">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{t("auth.verifyEmail.title")}</CardTitle>
          <CardDescription>
            {status === "verifying" && t("auth.verifyEmail.verifying")}
            {status === "success" && t("auth.verifyEmail.successDescription")}
            {status === "error" && t("auth.verifyEmail.errorDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-6">
          {status === "verifying" && <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />}
          {status === "success" && <CheckCircle2 className="h-12 w-12 text-green-500" />}
          {status === "error" && <XCircle className="h-12 w-12 text-destructive" />}

          {status === "success" && (
            <Button className="w-full" onClick={() => navigate("/")}>
              {t("auth.verifyEmail.continue")}
            </Button>
          )}

          {status === "error" && (
            <p className="text-center text-sm text-muted-foreground">
              {t("auth.verifyEmail.errorHelp")}{" "}
              <Link to="/signin" className="text-foreground hover:underline font-medium">
                {t("auth.verifyEmail.backToSignIn")}
              </Link>
            </p>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  );
};

export default VerifyEmail;
